import React, { useState, useEffect } from 'react';
import {
  Box,
  CircularProgress,
  Typography,
  IconButton,
  Paper,
  Alert,
  useTheme,
  Tooltip,
} from '@mui/material';
import ButtonMAIQ from 'app/components/ReusableUIComponents/LowLevelComponents/buttons/ButtonMAIQ';
import SaveIcon from '@mui/icons-material/Save';
import CloseIcon from '@mui/icons-material/Close';
import LocalOfferIcon from '@mui/icons-material/LocalOffer';
import ModalMAIQ from 'app/components/ModalMAIQ';
import { ValueContent } from 'app/apiService/valueContent/valueContentApiService';
import { parseDateValue, getTodayStartOfDay } from 'utils/dateUtils';
import ValueTypeEnums from './ValueTypeEnums';
import ValueForm, { ValueFormData, ValueFormErrors } from './components/ValueForm';

interface AddValueContentProps {
  open: boolean;
  onClose: () => void;
  onSave: (data: Partial<ValueContent>) => Promise<void>;
  initialData?: ValueContent | null;
  isEditing?: boolean;
}

const AddValueContent: React.FC<AddValueContentProps> = ({
  open,
  onClose,
  onSave,
  initialData = null,
  isEditing = false,
}) => {
  const theme = useTheme();

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string>('');
  const [formErrors, setFormErrors] = useState<ValueFormErrors>({});

  const [formData, setFormData] = useState<ValueFormData>({
    headline: initialData?.headline || '',
    content: initialData?.content || '',
    value_type: initialData?.value_type || ValueTypeEnums.CPO,
    applicable_for: initialData?.applicable_for || [],
    end_date: initialData?.end_date || '',
    is_active: initialData?.is_active ?? true,
    label: initialData?.label || '',
  });

  useEffect(() => {
    if (initialData) {
      setFormData({
        headline: initialData.headline || '',
        content: initialData.content || '',
        value_type: initialData.value_type || ValueTypeEnums.CPO,
        applicable_for: initialData.applicable_for || [],
        end_date: initialData.end_date || '',
        is_active: initialData.is_active ?? true,
        label: initialData.label || '',
      });
    } else {
      setFormData({
        headline: '',
        content: '',
        value_type: ValueTypeEnums.CPO,
        applicable_for: [],
        end_date: '',
        is_active: true,
        label: '',
      });
    }
    setError('');
    setFormErrors({});
  }, [initialData, open]);

  const handleFormChange = (field: string, value: any) => {
    setFormData(prev => ({
      ...prev,
      [field]: value,
    }));
    // Clear field-specific error when user starts typing
    if (formErrors[field as keyof ValueFormErrors]) {
      setFormErrors(prev => {
        const newErrors = { ...prev };
        delete newErrors[field as keyof ValueFormErrors];
        return newErrors;
      });
    }
    if (error) {
      setError('');
    }
  };

  const validateForm = (): boolean => {
    const errors: ValueFormErrors = {};

    if (!formData.headline?.trim()) {
      errors.headline = 'Headline is required';
    }
    if (!formData.content?.trim()) {
      errors.content = 'Message content is required';
    }
    if (!formData.value_type?.trim()) {
      errors.value_type = 'Value type is required';
    }
    if (formData.applicable_for.length === 0) {
      errors.applicable_for = 'At least one applicable option must be selected';
    }
    
    // Validate label field - optional but must be under 40 characters if provided
    if (formData.label && formData.label.length > 40) {
      errors.label = 'Label must be 40 characters or less';
    }
    
    // Validate expiry date - now required
    if (!formData.end_date?.trim()) {
      errors.end_date = 'Expiry date is required';
    } else {
      const selectedDate = parseDateValue(formData.end_date);
      if (!selectedDate) {
        errors.end_date = 'Expiry date is invalid';
      } else {
        const todayStart = getTodayStartOfDay();
        if (selectedDate.startOf('day').toMillis() < todayStart.toMillis()) {
          errors.end_date = 'Expiry date cannot be in the past';
        }
      }
    }

    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleSubmit = async () => {
    if (!validateForm()) return;

    setIsSubmitting(true);
    setError('');

    try {
      const payload = {
        headline: formData.headline.trim(),
        content: formData.content.trim(),
        value_type: formData.value_type,
        applicable_for: formData.applicable_for,
        end_date: formData.end_date || undefined,
        is_active: formData.is_active,
        label: formData.label?.trim() || undefined,
      };

      await onSave(payload);
      handleClose();
    } catch (err) {
      setError(
        err instanceof Error ? err.message : 'Failed to save value content',
      );
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleClose = () => {
    setFormData({
      headline: '',
      content: '',
      value_type: ValueTypeEnums.CPO,
      applicable_for: [],
      end_date: '',
      is_active: true,
      label: '',
    });
    setError('');
    onClose();
  };

  

  return (
    <ModalMAIQ
      open={open}
      onClose={handleClose}
      hideDefaultCloseButton={true}
    >
      <Paper
        elevation={0}
        sx={{
          width: '900px',
          maxWidth: '90vw',
          maxHeight: '85vh',
          display: 'flex',
          flexDirection: 'column',
          borderRadius: 0,
        }}
      >
        {/* Header - fixed */}
        <Box
          className="modal-header"
          sx={{
            p: 2,
            zIndex: 10,
            flexShrink: 0,
          }}
        >
          <Box
            display="flex"
            justifyContent="space-between"
            alignItems="center"
            width="100%"
          >
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <LocalOfferIcon className="modal-header-icon" />
              <Typography variant="h6" fontWeight="600">
                {isEditing ? 'Edit Value Content' : 'Add New Value Content'}
              </Typography>
            </Box>
            <Tooltip title="Close">
              <IconButton
                className="modal-header-button"
                aria-label="close"
                onClick={handleClose}
                sx={{
                  p: 0.5,
                }}
                size="small"
              >
                <CloseIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          </Box>
        </Box>

        {error && (
          <Alert severity="error" sx={{ mx: 2, mt: 2 }}>
            {error}
          </Alert>
        )}

        {/* Scrollable content area */}
        <Box
          sx={{
            flex: 1,
            overflowY: 'auto',
            position: 'relative',
            p: 3,
          }}
        >
          <ValueForm
            formData={formData}
            onFormChange={handleFormChange}
            errors={formErrors}
            disabled={isSubmitting}
          />
        </Box>

        {/* Footer */}
        <Box
          sx={{
            p: 2,
            display: 'flex',
            justifyContent: 'flex-end',
            backgroundColor: theme.palette.background.paper,
            borderTop: `1px solid ${theme.palette.divider}`,
            zIndex: 9,
            flexShrink: 0,
            gap: 2,
          }}
        >
           <ButtonMAIQ
                variant="outlined"
                onClick={handleClose}
                disabled={isSubmitting}
              >
                Cancel
              </ButtonMAIQ>
            <ButtonMAIQ
              variant="contained"
              color="primary"
              disabled={isSubmitting}
              onClick={handleSubmit}
              startIcon={
                isSubmitting ? (
                  <CircularProgress size={16} color="inherit" />
                ) : (
                  <SaveIcon />
                )
              }
            >
              {isSubmitting ? (isEditing ? 'Updating...' : 'Creating...') : (isEditing ? 'Update' : 'Create Value')}
            </ButtonMAIQ>
        </Box>
      </Paper>
    </ModalMAIQ>
  );
};

export default AddValueContent; 